import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/reducers';
import { fetchUsersRequest, fetchRolesRequest, updateRoleRequest, createRoleRequest, deleteRoleRequest } from '../store/users/actions';
import { toast } from 'react-toastify';
import { Shield, Plus, Save, X } from 'lucide-react';
import './AdminRoles.css';

// Mapping between API permission keys and display labels
const permissionLabels: Record<string, string> = {
  televerser: 'Téléverser',
  anonymiser: 'Anonymiser',
  valider: 'Valider',
  publier: 'Publier',
  gerer_utilisateurs: 'Gérer utilisateurs',
  configurer_workflow: 'Configurer workflow',
  voir_statistiques: 'Voir statistiques',
};

const allPermissionKeys = Object.keys(permissionLabels);

// Display labels for role names (API name → human label)
const getRoleLabel = (name: string): string => {
  const labels: Record<string, string> = {
    agent: 'Agent',
    admin: 'Administrateur',
    superadmin: 'Super Admin',
    validateur: 'Validateur',
    superviseur: 'Superviseur',
    user: 'Utilisateur',
  };
  return labels[name] || name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getRoleColor = (name: string): string => {
  const colors: Record<string, string> = {
    admin: '#DC2626',
    superadmin: '#DC2626',
    agent: '#8B6914',
    validateur: '#2563EB',
    superviseur: '#7C3AED',
    user: '#6B7280',
  };
  return colors[name] || '#8B6914';
};

const getRoleBg = (name: string): string => {
  const bgs: Record<string, string> = {
    admin: '#FEF2F2',
    superadmin: '#FEF2F2',
    agent: '#FFF8E7',
    validateur: '#EFF6FF',
    superviseur: '#F5F3FF',
    user: '#F3F4F6',
  };
  return bgs[name] || '#FFF8E7';
};

// Roles that cannot be deleted (system roles)
const PROTECTED_ROLES = ['admin', 'superadmin', 'agent'];

const AdminRoles: React.FC = () => {
  const dispatch = useDispatch();
  const { users, roles, isSubmitting } = useSelector((state: RootState) => state.users);

  const [showNewRole, setShowNewRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState<string[]>([]);

  useEffect(() => {
    dispatch(fetchUsersRequest({ limit: 200 }));
    dispatch(fetchRolesRequest());
  }, [dispatch]);

  // Build matrix from API roles
  const matrix = useMemo(() => {
    const m: Record<string, Record<string, boolean>> = {};
    roles.forEach(role => {
      const perms: Record<string, boolean> = {};
      allPermissionKeys.forEach(key => {
        perms[key] = (role.permissions || []).includes(key);
      });
      m[role.name] = perms;
    });
    return m;
  }, [roles]);

  // Role names from API
  const roleColumns = useMemo(() => roles.map(r => r.name), [roles]);

  // Count users per role
  const getRoleUserCount = (roleName: string): number => {
    return users.filter(u =>
      (u.role || []).some(r => r.name === roleName)
    ).length;
  };

  // Get active permissions for a role (for card display)
  const getRolePermissions = (roleName: string): string[] => {
    const perms = matrix[roleName];
    if (!perms) return [];
    return Object.entries(perms)
      .filter(([, v]) => v)
      .map(([k]) => permissionLabels[k] || k);
  };

  const handleToggle = (roleName: string, permKey: string) => {
    const role = roles.find(r => r.name === roleName);
    if (!role) return;
    const currentPerms = role.permissions || [];
    const newPerms = currentPerms.includes(permKey)
      ? currentPerms.filter(p => p !== permKey)
      : [...currentPerms, permKey];

    dispatch(updateRoleRequest(role.id, { permissions: newPerms }, () => {
      dispatch(fetchRolesRequest());
    }));
  };

  const handleCreateRole = () => {
    if (!newRoleName.trim()) {
      toast.error('Veuillez saisir un nom de rôle.');
      return;
    }
    dispatch(createRoleRequest(
      { name: newRoleName.trim().toLowerCase().replace(/\s+/g, '_'), permissions: newRolePermissions },
      () => {
        setShowNewRole(false);
        setNewRoleName('');
        setNewRolePermissions([]);
        dispatch(fetchRolesRequest());
      }
    ));
  };

  const handleDeleteRole = (roleId: number, roleName: string) => {
    if (!window.confirm(`Supprimer le rôle "${getRoleLabel(roleName)}" ?`)) return;
    dispatch(deleteRoleRequest(roleId, () => {
      dispatch(fetchRolesRequest());
    }));
  };

  const toggleNewRolePerm = (key: string) => {
    setNewRolePermissions(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  return (
    <div className="admin-roles">
      {/* Role summary cards */}
      <div className="admin-roles-cards">
        {roleColumns.map(roleName => {
          const count = getRoleUserCount(roleName);
          const perms = getRolePermissions(roleName);
          const visiblePerms = perms.slice(0, 3);
          const moreCount = perms.length - 3;
          const color = getRoleColor(roleName);
          const bg = getRoleBg(roleName);

          return (
            <div key={roleName} className="admin-role-card">
              <div className="admin-role-card-header">
                <div>
                  <span className="admin-role-card-label" style={{ color }}>
                    {getRoleLabel(roleName).toUpperCase()}
                  </span>
                  <div className="admin-role-card-count">{count}</div>
                  <span className="admin-role-card-sub">utilisateur{count > 1 ? 's' : ''}</span>
                </div>
                <div className="admin-role-card-icon" style={{ color, background: bg }}>
                  <Shield size={22} />
                </div>
              </div>
              <div className="admin-role-card-perms">
                {perms.length === 0 ? (
                  <span className="admin-role-perm-none">Aucune permission</span>
                ) : (
                  <>
                    {visiblePerms.map(p => (
                      <span key={p} className="admin-role-perm-tag" style={{ color, background: bg }}>{p}</span>
                    ))}
                    {moreCount > 0 && (
                      <span className="admin-role-perm-more">+{moreCount}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Permissions matrix */}
      <div className="admin-roles-matrix">
        <div className="admin-roles-matrix-header">
          <div>
            <h3>
              <Shield size={16} className="admin-roles-matrix-icon" />
              Matrice de permissions
            </h3>
            <p>Définissez les droits associés à chaque rôle</p>
          </div>
          <div className="admin-roles-matrix-actions">
            <button
              className="admin-roles-btn-new"
              onClick={() => setShowNewRole(true)}
            >
              <Plus size={16} /> Nouveau rôle
            </button>
          </div>
        </div>

        {/* New role form */}
        {showNewRole && (
          <div className="admin-roles-new-form">
            <div className="admin-roles-new-form-header">
              <h4>Créer un nouveau rôle</h4>
              <button className="admin-roles-new-form-close" onClick={() => setShowNewRole(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="admin-roles-new-form-row">
              <input
                type="text"
                placeholder="Nom du rôle (ex: superviseur)"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                className="admin-roles-new-input"
              />
            </div>
            <div className="admin-roles-new-form-perms">
              {allPermissionKeys.map(key => (
                <label key={key} className="admin-roles-new-perm-label">
                  <input
                    type="checkbox"
                    checked={newRolePermissions.includes(key)}
                    onChange={() => toggleNewRolePerm(key)}
                  />
                  <span>{permissionLabels[key]}</span>
                </label>
              ))}
            </div>
            <button
              className="admin-roles-btn-save"
              onClick={handleCreateRole}
              disabled={isSubmitting}
            >
              <Save size={16} /> {isSubmitting ? 'Création...' : 'Créer le rôle'}
            </button>
          </div>
        )}

        <table className="admin-roles-table">
          <thead>
            <tr>
              <th>PERMISSION</th>
              {roleColumns.map(roleName => {
                const role = roles.find(r => r.name === roleName);
                const canDelete = role && !PROTECTED_ROLES.includes(roleName);
                return (
                  <th key={roleName} className="th-role">
                    <span className="th-role-label" style={{ color: getRoleColor(roleName) }}>
                      {getRoleLabel(roleName).toUpperCase()}
                    </span>
                    {canDelete && (
                      <button
                        className="admin-roles-delete-btn"
                        onClick={() => handleDeleteRole(role!.id, roleName)}
                        title={`Supprimer le rôle ${getRoleLabel(roleName)}`}
                      >
                        Supprimer
                      </button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {allPermissionKeys.map(permKey => (
              <tr key={permKey}>
                <td className="td-perm-name">{permissionLabels[permKey]}</td>
                {roleColumns.map(roleName => (
                  <td key={roleName} className="td-perm-check">
                    <label className="admin-roles-checkbox">
                      <input
                        type="checkbox"
                        checked={matrix[roleName]?.[permKey] || false}
                        onChange={() => handleToggle(roleName, permKey)}
                        disabled={isSubmitting}
                      />
                      <span className="admin-roles-checkmark" />
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminRoles;
